import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, ShieldCheck, UserCheck, Ban, Plus, Calendar } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { id: "users", label: "Users" },
  { id: "categories", label: "Categories" },
  { id: "events", label: "Events" },
];

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("users");
  return (
    <div data-testid="admin-page" className="min-h-screen bg-black text-white">
      <div className="border-b border-zinc-900 bg-black/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/forum" className="flex items-center gap-2 text-white hover:text-sky-400">
            <ArrowLeft className="w-4 h-4" /> <span className="font-mono text-xs uppercase tracking-widest">Forum</span>
          </Link>
          <div className="flex items-center gap-2 text-xs font-mono">
            <ShieldCheck className="w-4 h-4 text-sky-400" />
            <span className="text-sky-400 uppercase tracking-widest">Admin · {user?.name}</span>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <h1 className="font-display text-4xl md:text-5xl uppercase mb-8">Admin Panel</h1>
        <div className="flex gap-1 border-b border-zinc-900 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-5 py-3 text-xs font-mono uppercase tracking-widest border-b-2 ${tab === t.id ? "border-sky-400 text-white" : "border-transparent text-zinc-500 hover:text-white"}`}
            >
              {t.label}
            </button>
          ))}
        </div>
        {tab === "users" && <UsersTab />}
        {tab === "categories" && <CategoriesTab />}
        {tab === "events" && <EventsTab />}
      </div>
    </div>
  );
}

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [err, setErr] = useState("");
  const load = async () => {
    try { const { data } = await api.get("/admin/users"); setUsers(data); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, []);
  const setRole = async (uid, role) => { await api.patch(`/admin/users/${uid}`, null, { params: { role } }); await load(); };
  const del = async (uid) => { if (window.confirm("Delete user?")) { await api.delete(`/admin/users/${uid}`); await load(); } };
  return (
    <div>
      {err && <div className="text-red-400 text-xs font-mono mb-4">{err}</div>}
      <table className="w-full border border-zinc-900" data-testid="admin-users-table">
        <thead>
          <tr className="border-b border-zinc-900 text-left">
            {["User", "Email", "Role", "Actions"].map((h) => <th key={h} className="p-3 font-mono text-[10px] uppercase tracking-widest text-zinc-500">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} data-testid={`admin-user-${u.id}`} className="border-b border-zinc-900">
              <td className="p-3 font-medium">{u.name}</td>
              <td className="p-3 text-zinc-400 text-sm">{u.email}</td>
              <td className="p-3"><span className={`text-xs font-mono uppercase tracking-widest ${u.role === "admin" ? "text-sky-400" : u.role === "banned" ? "text-red-400" : u.role === "pending" ? "text-yellow-400" : "text-white"}`}>{u.role}</span></td>
              <td className="p-3 flex gap-1 flex-wrap">
                {u.role === "pending" && <button onClick={() => setRole(u.id, "member")} data-testid={`admin-approve-${u.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-sky-800 text-sky-400 hover:border-sky-400"><UserCheck className="w-3 h-3 inline mr-1" />Approve</button>}
                {u.role !== "banned" && u.role !== "admin" && <button onClick={() => setRole(u.id, "banned")} data-testid={`admin-ban-${u.id}`} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-red-900 text-red-400 hover:border-red-500"><Ban className="w-3 h-3 inline mr-1" />Ban</button>}
                {u.role === "banned" && <button onClick={() => setRole(u.id, "member")} className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-zinc-800 hover:border-sky-400 hover:text-sky-400">Unban</button>}
                {u.role !== "admin" && <button onClick={() => del(u.id)} data-testid={`admin-delete-${u.id}`} className="text-red-400 hover:text-red-300 p-1"><Trash2 className="w-3.5 h-3.5" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoriesTab() {
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const load = async () => { const { data } = await api.get("/forum/categories"); setCats(data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/forum/categories", { name, description: desc, order: cats.length + 1 }); setName(""); setDesc(""); await load(); };
  const del = async (id) => { if (window.confirm("Delete category and all threads inside?")) { await api.delete(`/forum/categories/${id}`); await load(); } };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="border border-zinc-800 p-4 flex flex-wrap gap-3 items-end" data-testid="admin-new-category-form">
        <input required minLength={2} maxLength={60} placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid="admin-cat-name" className="bg-black border border-zinc-800 text-white px-3 py-2 flex-1 min-w-[200px] focus:outline-none focus:border-sky-400 text-sm" />
        <input maxLength={200} placeholder="Description" value={desc} onChange={(e) => setDesc(e.target.value)} data-testid="admin-cat-desc" className="bg-black border border-zinc-800 text-white px-3 py-2 flex-1 min-w-[240px] focus:outline-none focus:border-sky-400 text-sm" />
        <button type="submit" data-testid="admin-cat-add" className="bg-sky-400 text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-sky-300"><Plus className="w-3 h-3 inline mr-1" />Add</button>
      </form>
      <ul className="space-y-2">
        {cats.map((c) => (
          <li key={c.id} className="border border-zinc-900 p-4 flex items-center justify-between">
            <div>
              <div className="font-bold uppercase">{c.name}</div>
              <div className="text-xs text-zinc-500">{c.description}</div>
            </div>
            <button onClick={() => del(c.id)} data-testid={`admin-cat-del-${c.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [date, setDate] = useState("");
  const load = async () => { const { data } = await api.get("/events"); setEvents(data); };
  useEffect(() => { load(); }, []);
  const add = async (e) => { e.preventDefault(); await api.post("/events", { title, body, date: new Date(date).toISOString() }); setTitle(""); setBody(""); setDate(""); await load(); };
  const del = async (id) => { if (window.confirm("Delete event?")) { await api.delete(`/events/${id}`); await load(); } };
  return (
    <div className="space-y-6">
      <form onSubmit={add} className="border border-zinc-800 p-4 space-y-3" data-testid="admin-new-event-form">
        <input required minLength={2} maxLength={140} placeholder="Title (e.g. CS2 Tournament Night)" value={title} onChange={(e) => setTitle(e.target.value)} data-testid="admin-event-title" className="w-full bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-sky-400 text-sm" />
        <input required type="datetime-local" value={date} onChange={(e) => setDate(e.target.value)} data-testid="admin-event-date" className="bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-sky-400 text-sm" />
        <textarea rows={3} maxLength={4000} placeholder="Details" value={body} onChange={(e) => setBody(e.target.value)} data-testid="admin-event-body" className="w-full bg-black border border-zinc-800 text-white px-3 py-2 focus:outline-none focus:border-sky-400 text-sm font-mono" />
        <button type="submit" data-testid="admin-event-add" className="bg-sky-400 text-black px-4 py-2 text-xs font-bold uppercase tracking-widest hover:bg-sky-300"><Calendar className="w-3 h-3 inline mr-1" />Publish</button>
      </form>
      <ul className="space-y-2">
        {events.map((e) => (
          <li key={e.id} className="border border-zinc-900 p-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-bold uppercase">{e.title}</div>
                <div className="text-xs text-sky-400 font-mono">{new Date(e.date).toLocaleString()}</div>
                <div className="text-sm text-zinc-400 mt-2 whitespace-pre-wrap">{e.body}</div>
              </div>
              <button onClick={() => del(e.id)} data-testid={`admin-event-del-${e.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
